import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import withLoading from 'hocs/withLoading';
import _ from 'lodash';

import useTableCellPendingChangeAuthors from 'hooks/useTableCellPendingChangeAuthors';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { deleteLoanAssetAction } from 'store/loanApplications/actions';
import { selectSortedBorrowerAssets } from 'store/loanApplications/selectors';

import { normalizeAssetsPendingChanges } from '../../UpdateTableModal/Content/SectionContent/Assets/Assets.utils';
import {
  FINANCIAL_INFO_TABLE_NAME,
  getDataWithNotFoundMessage,
  TablePropTypes,
} from '../FinancialInfoBody.constants';
import { tableStyles } from '../FinancialInfoBody.styles';
import { getTotalPendingChangesCount } from '../FinancialInfoBody.utils';
import useNeededCountByRecord from '../hooks/useNeededCountByRecord';
import TableWrapper from '../TableWrapper';

import { getConfig } from './Assets.config';

const Assets = ({
  onToggle,
  tableName,
  borrowerId,
  isLoading,
  setIsLoading,
  onAddRecord,
  pendingChanges,
  chatTargets = null,
}) => {
  const assets = useSelector(selectSortedBorrowerAssets(borrowerId));
  const { subtab, applicationId } = useParams();

  const dispatch = useDispatch();

  const isExpanded = subtab === tableName;

  const onAddClick = useCallback(() => onAddRecord(tableName), [onAddRecord, tableName]);

  const onRemove = useCallback(
    recordId => {
      setIsLoading(true);

      dispatch(
        deleteLoanAssetAction({
          assetId: recordId,
          tabId: LoanApplicationTabId.ASSETS,
          applicationId,
        }),
      )
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, setIsLoading],
  );

  const neededCountById = useNeededCountByRecord(tableName, assets);
  const getCellPendingChangeAuthors = useTableCellPendingChangeAuthors();

  const config = useMemo(
    () => getConfig({ isExpanded, onRemove, chatTargets, neededCountById }),
    [isExpanded, onRemove, chatTargets, neededCountById],
  );

  const formattedData = assets.length ? assets : getDataWithNotFoundMessage(config)[tableName];

  const assetIds = useMemo(() => assets.map(({ id }) => id), [assets]);

  const customFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.ASSET,
    entityIds: assetIds,
  });

  const pendingChangesCounts = calculatePendingChanges(
    _.merge(
      {},
      normalizeAssetsPendingChanges({
        initialPendingChanges: pendingChanges,
      }),
      customFieldsPendingChanges,
    ),
    true,
  );

  const totalPendingChangesCount = getTotalPendingChangesCount({
    pendingChangesCounts,
    data: assets,
  });

  return (
    <TableWrapper tableName={tableName} onToggle={onToggle}>
      <TableCDS
        withAddButton
        config={config}
        customStyles={tableStyles}
        data={formattedData}
        getCellPendingChangeAuthors={getCellPendingChangeAuthors}
        isCustomDataDefinition={!assets.length}
        isLoading={isLoading}
        isPreview={!isExpanded}
        pendingChangesCount={totalPendingChangesCount}
        title={FINANCIAL_INFO_TABLE_NAME[tableName]}
        onAddClick={onAddClick}
      />
    </TableWrapper>
  );
};

Assets.propTypes = {
  ...TablePropTypes,
};

export default withLoading(Assets);
